/**
 * SaveStatus Component
 *
 * Displays the current save status for draft editing.
 * Shows when content was last saved and current save state.
 */

import { Check, AlertCircle, Loader2, Cloud, CloudOff } from "lucide-react";
import type { ReactElement } from "react";

import type { SaveStatus as SaveStatusType } from "../../hooks/use-draft-management";

import { LockIndicatorCompact } from "./LockIndicator";

// ============================================================================
// Types
// ============================================================================

/** Save status indicator props */
export interface SaveStatusProps {
  /** Current save status */
  readonly status: SaveStatusType;
  /** Whether content has unsaved changes */
  readonly isDirty?: boolean | undefined;
  /** Timestamp of last successful save */
  readonly lastSavedAt?: string | null | undefined;
  /** Error message if save failed */
  readonly error?: string | null | undefined;
  /** Additional CSS classes */
  readonly className?: string | undefined;
  /** Click handler for manual save */
  readonly onSave?: (() => void) | undefined;
}

// ============================================================================
// Status Configuration
// ============================================================================

interface StatusConfig {
  readonly icon: typeof Check;
  readonly label: string;
  readonly bgColor: string;
  readonly textColor: string;
  readonly iconColor: string;
  readonly showPulse?: boolean;
}

const STATUS_CONFIG: Record<SaveStatusType, StatusConfig> = {
  idle: {
    icon: Cloud,
    label: "Ready",
    bgColor: "bg-gray-100",
    textColor: "text-gray-600",
    iconColor: "text-gray-400",
  },
  saving: {
    icon: Loader2,
    label: "Saving...",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    iconColor: "text-blue-500",
    showPulse: true,
  },
  saved: {
    icon: Check,
    label: "Saved",
    bgColor: "bg-green-50",
    textColor: "text-green-700",
    iconColor: "text-green-500",
  },
  error: {
    icon: AlertCircle,
    label: "Save failed",
    bgColor: "bg-red-50",
    textColor: "text-red-700",
    iconColor: "text-red-500",
  },
};

// ============================================================================
// Component
// ============================================================================

/**
 * SaveStatus - Shows draft save status
 *
 * CSS Classes:
 * - save-status: Container element
 * - save-status--idle: Idle state
 * - save-status--saving: Saving in progress
 * - save-status--saved: Successfully saved
 * - save-status--error: Save error
 * - save-status--dirty: Has unsaved changes
 */
export function SaveStatus({
  status,
  isDirty = false,
  lastSavedAt,
  error,
  className = "",
  onSave,
}: SaveStatusProps): ReactElement {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  const isSpinning = status === "saving";

  // Format last saved time
  const lastSavedDisplay = lastSavedAt ? formatTimeAgo(new Date(lastSavedAt)) : undefined;

  // Determine display label
  let displayLabel = config.label;
  if (status === "idle" && isDirty) {
    displayLabel = "Unsaved changes";
  } else if (status === "saved" && lastSavedDisplay) {
    displayLabel = `Saved ${lastSavedDisplay}`;
  } else if (status === "error" && error) {
    displayLabel = error;
  }

  // Dynamic styling for dirty state
  const dirtyClasses =
    isDirty && status === "idle"
      ? "bg-amber-50 text-amber-700"
      : `${config.bgColor} ${config.textColor}`;

  const dirtyIconColor = isDirty && status === "idle" ? "text-amber-500" : config.iconColor;

  return (
    <div
      className={`save-status save-status--${status} ${isDirty ? "save-status--dirty" : ""} inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm ${dirtyClasses} ${className}`}
    >
      {/* Status Icon */}
      {isDirty && status === "idle" ? (
        <CloudOff className={`h-4 w-4 ${dirtyIconColor}`} aria-hidden="true" />
      ) : (
        <Icon
          className={`h-4 w-4 ${config.iconColor} ${isSpinning ? "animate-spin" : ""}`}
          aria-hidden="true"
        />
      )}

      {/* Status Label */}
      <span className="font-medium truncate max-w-[200px]" title={error ?? undefined}>
        {displayLabel}
      </span>

      {/* Manual Save Button */}
      {isDirty && status !== "saving" && onSave && (
        <button
          type="button"
          onClick={onSave}
          className="ml-1 rounded bg-blue-500 px-2 py-0.5 text-xs font-medium text-white hover:bg-blue-600 transition-colors"
        >
          Save
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Compact Variant
// ============================================================================

/**
 * Compact save status showing just an icon.
 */
export function SaveStatusCompact({
  status,
  isDirty,
  lastSavedAt,
  error,
  className = "",
}: Omit<SaveStatusProps, "onSave">): ReactElement {
  const config = STATUS_CONFIG[status];
  const Icon = isDirty && status === "idle" ? CloudOff : config.icon;
  const isSpinning = status === "saving";

  // Build tooltip
  let title = config.label;
  if (status === "idle" && isDirty) {
    title = "Unsaved changes";
  } else if (status === "saved" && lastSavedAt) {
    title = `Saved ${formatTimeAgo(new Date(lastSavedAt))}`;
  } else if (status === "error" && error) {
    title = error;
  }

  const bgColor = isDirty && status === "idle" ? "bg-amber-50" : config.bgColor;
  const iconColor = isDirty && status === "idle" ? "text-amber-500" : config.iconColor;

  return (
    <div
      className={`save-status-compact inline-flex items-center justify-center rounded-full p-1.5 ${bgColor} ${className}`}
      title={title}
      role="status"
      aria-label={title}
    >
      <Icon
        className={`h-4 w-4 ${iconColor} ${isSpinning ? "animate-spin" : ""}`}
        aria-hidden="true"
      />
    </div>
  );
}

// ============================================================================
// Combined Status Bar
// ============================================================================

/** Props for the combined status bar */
export interface DraftStatusBarProps {
  /** Save status */
  readonly saveStatus: SaveStatusType;
  /** Whether content is dirty */
  readonly isDirty: boolean;
  /** Last saved timestamp */
  readonly lastSavedAt?: string | null | undefined;
  /** Save error message */
  readonly saveError?: string | null | undefined;
  /** Lock status */
  readonly lockStatus: "unlocked" | "acquiring" | "locked" | "locked_by_other" | "error";
  /** Info about who holds the lock */
  readonly heldByInfo?:
    | { name?: string | undefined; email?: string | undefined; userId: string }
    | null
    | undefined;
  /** Additional CSS classes */
  readonly className?: string | undefined;
  /** Handler to trigger save */
  readonly onSave?: (() => void) | undefined;
}

/**
 * Combined status bar showing both save and lock status.
 */
export function DraftStatusBar({
  saveStatus,
  isDirty,
  lastSavedAt,
  saveError,
  lockStatus,
  heldByInfo,
  className = "",
  onSave,
}: DraftStatusBarProps): ReactElement {
  // LockIndicatorCompact is imported at top level — no circular dependency exists
  // between SaveStatus and LockIndicator (they are sibling modules).

  return (
    <div className={`draft-status-bar flex items-center gap-2 ${className}`}>
      <LockIndicatorCompact status={lockStatus} heldByInfo={heldByInfo} />
      <SaveStatus
        status={saveStatus}
        isDirty={isDirty}
        lastSavedAt={lastSavedAt}
        error={saveError}
        onSave={onSave}
      />
    </div>
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format a date as a relative time string (e.g., "just now", "2m ago").
 */
function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 0) {
    return "just now";
  }

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (seconds < 10) {
    return "just now";
  }

  if (seconds < 60) {
    return `${seconds}s ago`;
  }

  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  if (hours < 24) {
    return `${hours}h ago`;
  }

  return date.toLocaleDateString();
}

export default SaveStatus;
